import ROUTE_SLUGS_JSON from './routeSlugs.json';
export const LANGS = ['es', 'ca', 'en', 'fr'] as const;
export type Lang = (typeof LANGS)[number];
export const DEFAULT_LANG: Lang = 'es';

/**
 * URL pública de cada página en cada idioma (src/i18n/routeSlugs.json).
 * Clave = ruta interna que usa el código ("/cie"); valor = slug público ("/boletin-electrico-cie").
 * Las URLs antiguas en inglés redirigen a estas (ver App.tsx y scripts/prerender.mjs).
 */
export const ROUTE_SLUGS = ROUTE_SLUGS_JSON as Record<string, Record<Lang, string>>;

/** Ruta pública sin idioma -> ruta interna: "/boletin-electrico-cie" -> "/cie" (las antiguas también) */
const toInternal = (p: string): string => {
  for (const [key, bySlug] of Object.entries(ROUTE_SLUGS)) {
    if (key === p || Object.values(bySlug).includes(p)) return key;
  }
  return p;
};

/** Ruta interna -> slug público en un idioma: ("/cie", "es") -> "/boletin-electrico-cie" */
export const publicSlug = (p: string, lang: Lang): string => ROUTE_SLUGS[p]?.[lang] ?? p;
export const PREFIXED_LANGS: Lang[] = ['ca', 'en', 'fr'];

export const HTML_LANG: Record<Lang, string> = { es: 'es', ca: 'ca', en: 'en', fr: 'fr' };
export const OG_LOCALE: Record<Lang, string> = { es: 'es_ES', ca: 'ca_ES', en: 'en_GB', fr: 'fr_FR' };
export const DATE_LOCALE: Record<Lang, string> = { es: 'es-ES', ca: 'ca-ES', en: 'en-GB', fr: 'fr-FR' };

export const normalizeLang = (lng?: string | null): Lang => {
  const base = (lng || '').split('-')[0] as Lang;
  return (LANGS as readonly string[]).includes(base) ? base : DEFAULT_LANG;
};

export const getLangFromPath = (pathname: string): Lang => {
  const first = pathname.split('/')[1];
  return (PREFIXED_LANGS as string[]).includes(first) ? (first as Lang) : DEFAULT_LANG;
};

/** Quita el prefijo de idioma: "/en/blog" -> "/blog", "/fr" -> "/" */
export const stripLang = (rawPath: string): string => {
  // Quita la barra final ("/cie/" -> "/cie") para que coincida con las claves de rutas
  const pathname = rawPath && rawPath.length > 1 ? rawPath.replace(/\/+$/, '') || '/' : rawPath;
  const first = pathname.split('/')[1];
  if ((PREFIXED_LANGS as string[]).includes(first)) {
    const rest = pathname.slice(first.length + 1);
    return rest === '' ? '/' : toInternal(rest);
  }
  return toInternal(pathname || '/');
};

/** Añade el prefijo de idioma a una ruta interna: ("/blog", "en") -> "/en/blog" */
export const localizePath = (path: string, lng?: string | null): string => {
  if (!path.startsWith('/')) return path;
  const lang = normalizeLang(lng);
  const clean = publicSlug(stripLang(path), lang);
  if (lang === DEFAULT_LANG) return clean;
  return clean === '/' ? '/' + lang : '/' + lang + clean;
};
